import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, ArrowRight, FileText } from "lucide-react";
import { trackPurchase } from "../lib/ga";

/**
 * Public landing page shown after a successful Stripe Checkout session
 * (magic-link flow). Reachable without login. Reads ?inv=&already= from URL.
 */
export default function PaySuccess() {
  const [params] = useSearchParams();
  const invoice = params.get("inv") || "";
  const already = params.get("already") === "1";

  useEffect(() => {
    document.title = `Pago confirmado · Velcor Energy`;
    // Fire purchase conversion. We don't have the exact amount in the URL
    // (Stripe doesn't pass it back for privacy), so we report the invoice
    // ref and let Google Ads / GA4 attribute the value via Enhanced
    // Conversions on the server side. If `already=1` the payment didn't
    // generate revenue this session, so we skip.
    if (!already) {
      trackPurchase({ invoice });
    }
  }, [invoice, already]);

  return (
    <main
      className="min-h-screen grid place-items-center bg-[#f6f9f5] px-5 py-12"
      data-testid="pay-success-page"
    >
      {/* SEO: noindex — transactional page. Eligible as a Google Ads
          conversion URL but not as organic search result. */}
      <Helmet>
        <title>Pago confirmado — Velcor Energy</title>
        <meta name="description" content="Tu pago fue procesado de forma segura por Stripe. Velcor Energy confirma tu transacción." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <article className="bg-white rounded-3xl border border-emerald-200 max-w-[520px] w-full p-8 md:p-12 text-center shadow-sm">
        <div className="mx-auto mb-6 grid place-items-center w-20 h-20 rounded-full bg-emerald-50">
          <CheckCircle2 className="w-12 h-12 text-emerald-600" strokeWidth={1.6} />
        </div>
        <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-emerald-700 mb-2">
          Pago confirmado
        </div>
        <h1
          className="display text-velcor-ink leading-tight mb-4"
          style={{ fontSize: "clamp(26px, 4vw, 36px)", fontWeight: 600, letterSpacing: "-0.02em" }}
        >
          {already ? "Esta factura ya estaba pagada." : "¡Gracias! Recibimos tu pago."}
        </h1>
        <p className="text-slate-500 text-[14.5px] leading-relaxed mb-2">
          {already
            ? "No se realizó ningún cargo nuevo — tu cuenta sigue al día."
            : "Tu pago fue procesado de forma segura por Stripe. Tu cuenta ahora figura como al día."}
        </p>
        {invoice && (
          <p className="text-slate-400 text-[12.5px] mb-7">
            Referencia: <strong className="text-velcor-ink tabular-nums">{invoice}</strong>
          </p>
        )}

        <div className="border-t border-slate-100 pt-7 flex flex-col gap-3">
          <Link
            to="/portal/login"
            data-testid="pay-success-portal-link"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-velcor-green text-white font-bold text-[14px] hover:bg-velcor-green/90 transition-colors"
          >
            Ir a mi portal <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/portal/messages"
            className="inline-flex items-center justify-center gap-2 text-slate-500 hover:text-velcor-ink text-[13px] transition-colors"
          >
            <FileText className="w-4 h-4" /> Descargar recibo desde mi bandeja
          </Link>
        </div>

        <p className="mt-9 text-[11px] text-slate-400 leading-relaxed">
          Velcor Energy, LLC · PUCT REP Certificate #VEL-2026 · (800) 715-6516
        </p>
      </article>
    </main>
  );
}
